import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'sonner';
import {
  rememberSessionEndReason,
  extractSessionEndReason,
} from './sessionEndReason';
import {
  classifyRefreshFailure,
  notifySessionRefreshed,
  RefreshStatuses,
  RefreshUnavailableError,
  type RefreshOutcome,
} from './refreshOutcome';
import { BILLING_ACCESS_BLOCKED_ERROR_KEY, BILLING_ACCESS_REASONS, type BillingAccessReason } from '../types/billing';
import type { RefreshResponse } from '../services/authApi';

export { AXIOS_CANCELED_CODE, isRequestCanceled } from './requestCancel';
import { isRequestCanceled } from './requestCancel';

/** Beetegre API istemcisi. JWT + tenant header'ları otomatik eklenir. */
// S5: withCredentials → HttpOnly refresh cookie requestlerle birlikte gönderilir.
// Frontend same-origin (nginx /api proxy) veya CORS AllowCredentials + specific origin ile çalışır.
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

// ── Request interceptor ──────────────────────────────────────────────────────
// R6: Seed an X-Correlation-Id per request so the end-to-end trace
// (browser click → nginx → API → Jobs/Worker → outgoing marketplace HTTP)
// starts at the user action. Backend CorrelationIdMiddleware honors it if set,
// otherwise stamps its own — both cases flow through Activity tags + BackgroundJobLog.
function newCorrelationId(): string {
  const g = globalThis as unknown as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  // Fallback for very old browsers (RFC 4122 v4 shape, not cryptographically strong).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

api.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const h = config.headers as Record<string, string> | undefined;
  if (h && !h['X-Correlation-Id'] && !h['x-correlation-id']) {
    (config.headers as Record<string, string>)['X-Correlation-Id'] = newCorrelationId();
  }
  return config;
});

// ── Response interceptor + P0-5 silent refresh ──────────────────────────────
// 401 alındığında: refresh token varsa bir kez /auth/refresh'i dener; eş
// zamanlı birden fazla 401 oluşursa hepsi aynı promise'i bekler.
// Refresh sonucu artik token'in yaninda basarisizlik sebebini de tasiyor: eszamanli 401'ler
// ayni promise'i bekledigi icin sebep de tek yerden gelmeli (AB#1735).
// Only `sessionEnded` may log the user out; `unavailable` (429 after the one allowed retry, 5xx,
// network) leaves the session untouched, because the server never said the credential is bad.
/**
 * Wait before the single 429 retry when Retry-After is missing, and the floor under it otherwise:
 * the server rounds the header to whole seconds, so "0" can arrive while the window is still shut.
 */
const RATE_LIMIT_MIN_DELAY_MS = 1000;
/**
 * Longest Retry-After we are willing to sit through while a request is held open. The server's
 * refresh limiter is a fixed one-minute window, so a real Retry-After is 0-60s; anything above
 * this cap skips the wait and goes straight to the "try again" state rather than looking like a
 * hang. Only a short throttle is therefore retried automatically.
 */
const RATE_LIMIT_MAX_WAIT_MS = 10_000;

const REFRESH_UNAVAILABLE_TOAST_ID = 'auth-refresh-unavailable';
const REFRESH_UNAVAILABLE_I18N_KEY = 'auth.session.refreshUnavailable';

let refreshInFlight: Promise<RefreshOutcome> | null = null;
let refreshUnavailableShown = false;

let sessionEndWatched = false;

// A session that ends (logout, or redirectToLogin while already on /login, where no reload follows)
// must not leave the persistent toast and its Retry action behind, nor a flag that would make the
// next session's first refresh refetch as if it had recovered from something. Installed on first
// use rather than at import: this module is imported (often transitively) under test mocks of the
// auth store that have no `subscribe`, and an import-time call crashes every such suite.
function watchSessionEnd() {
  if (sessionEndWatched) return;
  sessionEndWatched = true;
  useAuthStore.subscribe((state, prev) => {
    if (prev.token && !state.token && refreshUnavailableShown) {
      refreshUnavailableShown = false;
      toast.dismiss(REFRESH_UNAVAILABLE_TOAST_ID);
    }
  });
}

function refreshSingleFlight(): Promise<RefreshOutcome> {
  refreshInFlight ??= tryRefresh()
    .then((outcome) => {
      if (outcome.status === RefreshStatuses.refreshed) clearRefreshUnavailable();
      return outcome;
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

// Any successful refresh ends the "could not refresh" state, not only the toast's own action: the
// next 401 after a transient failure usually recovers on its own, and a toast left behind would
// claim a broken session that is already working (and leave the failed queries un-refetched).
function clearRefreshUnavailable() {
  if (!refreshUnavailableShown) return;
  refreshUnavailableShown = false;
  toast.dismiss(REFRESH_UNAVAILABLE_TOAST_ID);
  notifySessionRefreshed();
}

/** Ends the session in memory: clears the auth store and remembers why, without navigating. */
function endSession(reasonKey?: string | null) {
  useAuthStore.getState().logout();
  rememberSessionEndReason(reasonKey);
}

// Exported for the same reason `tryRefresh` below is: the interceptor pipeline has no test
// harness in this repo, and the returnTo behaviour is worth pinning directly.
export function redirectToLogin(reasonKey?: string | null) {
  endSession(reasonKey);
  if (!window.location.pathname.startsWith('/login')) {
    // AB#1864: mid-session expiry must preserve where the user was, exactly like RoleGuard's
    // bounce does. Without this the two unauthenticated paths behave differently — the guard
    // (never authenticated) keeps the destination, this one (token died en route) silently
    // dropped it. That gap is invisible on ordinary pages, where landing on /dashboard is a
    // shrug, and expensive on a deep link that cannot be reconstructed: a merchant returning
    // from Shopify's OAuth callback holds a single-use, 30-minute install cookie, and losing
    // the connect route means starting the whole install over on Shopify's side.
    //
    // The value is same-origin by construction (it is this document's own location), but it is
    // still read back through sanitizeReturnTo on the login side — that check stays the single
    // gate, so no caller has to be trusted.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  }
}

/**
 * One-time startup refresh for a persisted session whose in-memory access token is gone (every
 * page load: the token is deliberately not persisted). Reuses the single-flight refresh above, so
 * a request that 401s meanwhile joins the same call instead of starting a second one.
 *
 * `sessionRestoring` is raised synchronously (before the first await) so a caller that invokes
 * this before the first render has the guards on the skeleton from the very first paint, and it is
 * always cleared in `finally`: a refresh that throws must never leave the app on a skeleton.
 *  - refreshed: attemptRefresh already wrote the token and the server-state mirrors.
 *  - sessionEnded: log out and remember why; the route guard's own <Navigate> (which keeps the
 *    returnTo) takes the user to /login, no hard reload needed.
 *  - unavailable: the server did not say the session is over; keep it. The reactive 401 path and
 *    its retry toast cover the rest.
 */
export async function restoreSession(): Promise<void> {
  const auth = useAuthStore.getState();
  if (!auth.isAuthenticated || auth.token) return;
  auth.setSessionRestoring(true);
  try {
    const outcome = await refreshSingleFlight();
    if (outcome.status === RefreshStatuses.sessionEnded) {
      endSession(outcome.reasonKey);
    } else if (outcome.status === RefreshStatuses.unavailable) {
      console.warn('[API] Açılışta refresh geçici olarak başarısız – oturum korunuyor.');
    }
  } finally {
    useAuthStore.getState().setSessionRestoring(false);
  }
}

// Dynamic import, same reason as the network-error branch in the interceptor below.
async function notifyRefreshUnavailable(): Promise<RefreshUnavailableError> {
  // Raised before the await: a refresh that succeeds meanwhile clears it, and the toast is then
  // skipped instead of appearing over a session that has already recovered.
  refreshUnavailableShown = true;
  watchSessionEnd();
  const { default: i18n } = await import('./i18n');
  const message = i18n.t(REFRESH_UNAVAILABLE_I18N_KEY);
  if (!refreshUnavailableShown) return new RefreshUnavailableError(message);
  // Stable id: N concurrent 401s share one refresh but each reaches this point, and a repeat
  // call (the retry action failing again) replaces the toast instead of stacking another.
  toast.error(message, {
    id: REFRESH_UNAVAILABLE_TOAST_ID,
    // Re-runs the refresh instead of reloading the page, so unsaved form input survives.
    // Persist: the action is the whole point, and auto-dismiss would remove it. Cleared by the
    // next successful refresh (see clearRefreshUnavailable) or by the user.
    duration: Infinity,
    action: { label: i18n.t('common.actions.retry'), onClick: () => { void retryRefreshFromToast(); } },
  });
  return new RefreshUnavailableError(message);
}

async function retryRefreshFromToast(): Promise<void> {
  const outcome = await refreshSingleFlight();
  if (outcome.status === RefreshStatuses.sessionEnded) {
    redirectToLogin(outcome.reasonKey);
  } else if (outcome.status === RefreshStatuses.unavailable) {
    await notifyRefreshUnavailable();
  }
}

// Exported (alongside the default `api` client) so tests can exercise the refresh mirroring
// logic directly instead of reconstructing axios's interceptor/adapter pipeline (no
// axios-mock-adapter dependency in this repo). Same pattern as extractApiError.ts's
// `translateApiMessage` — an internal helper exported purely for direct unit testability.
export async function tryRefresh(): Promise<RefreshOutcome> {
  let outcome = await attemptRefresh();
  if (outcome.status === RefreshStatuses.unavailable && outcome.rateLimited) {
    const delay = Math.max(outcome.retryAfterMs ?? 0, RATE_LIMIT_MIN_DELAY_MS);
    if (delay > RATE_LIMIT_MAX_WAIT_MS) return { status: RefreshStatuses.unavailable };
    await new Promise((resolve) => setTimeout(resolve, delay));
    // Exactly one retry. A second 429 means the bucket is still drained; hammering it further is
    // the retry storm the rate limiter exists to stop. Transient failures (5xx/network) are not
    // retried here either: the user-facing "try again" state covers them without multiplying load.
    outcome = await attemptRefresh();
  }
  return outcome.status === RefreshStatuses.unavailable ? { status: RefreshStatuses.unavailable } : outcome;
}

async function attemptRefresh(): Promise<RefreshOutcome> {
  // S5: Refresh token HttpOnly cookie — body boş; cookie withCredentials ile gider.
  let resp: { data: RefreshResponse };
  try {
    resp = await axios.post<RefreshResponse>(
      (import.meta.env.VITE_API_URL || '') + '/api/v1/auth/refresh',
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
        withCredentials: true,
      }
    );
  } catch (e) {
    return classifyRefreshFailure(e);
  }
  // Only a rejected POST is classified. A 2xx the client cannot use (an HTML fallback page, a body
  // without a token) or a failure while mirroring it is not "the server could not answer", so it
  // ends the session as before rather than leaving the user on a retry toast that can never succeed.
  const newAccess: unknown = (resp.data as Partial<RefreshResponse> | null)?.accessToken;
  if (typeof newAccess !== 'string' || newAccess.length === 0) {
    return { status: RefreshStatuses.sessionEnded, reasonKey: null };
  }
  try {
    const auth = useAuthStore.getState();
    auth.setAccessToken(newAccess);
    // Keep authStore mirror of server-side state fresh on every refresh:
    // permissions, localizationSettings, memberships and billing may have changed in
    // another tab/admin session; rotating the token without rotating these
    // fields would leave stale UI gating until the next full login. Without
    // this, memberships only ever enters the store via setAuth (login /
    // switch-tenant), so a long-lived session that never re-logs-in would
    // keep showing a stale (possibly empty) membership list to the tenant
    // switcher indefinitely (AB#1471).
    if (Array.isArray(resp.data.permissions)) {
      auth.setPermissions(resp.data.permissions);
    }
    if (Array.isArray(resp.data.memberships)) {
      auth.setMemberships(resp.data.memberships);
    }
    if (resp.data.localizationSettings) {
      auth.setLocalizationSettings(resp.data.localizationSettings);
    }
    // Billing access gate (docs/design/billing-access-gate.md): before this, `billing` was the
    // one field this mirror skipped, so the only way a Blocked tenant ever saw Entitled again
    // was a fresh login/switch-tenant or a purchase's own post-success refresh — a TenantMember
    // (who never initiates either) stayed on the full-screen block indefinitely, and a single
    // transient 402 became a permanent one. `'billing' in resp.data` (not `resp.data.billing`)
    // because the field is legitimately null for SuperAdmin / a tenant-less session and that
    // null must overwrite a stale Blocked summary, not be skipped like a falsy guard would.
    if ('billing' in resp.data) {
      auth.setBillingAccess(resp.data.billing ?? null);
    }
    return { status: RefreshStatuses.refreshed, token: newAccess };
  } catch {
    return { status: RefreshStatuses.sessionEnded, reasonKey: null };
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    if (isRequestCanceled(err)) {
      return Promise.reject(err);
    }
    if (!err.response) {
      const code = (err as { code?: string }).code;
      const isTimeout = code === 'ECONNABORTED' || /timeout/i.test(err.message);
      const tag = isTimeout
        ? '[API] Yanıt zaman aşımı (timeout)'
        : '[API] Network hatası (sunucu erişilemiyor / CORS)';
      console.error(tag, err.message, err.config?.url);
      // Dynamic import, not a static one: api.ts is imported (directly or transitively) by
      // nearly every frontend test file. A static `import i18n from './i18n'` would pull in
      // i18next's `initReactI18next` at MODULE LOAD TIME for all of them, including files that
      // `vi.mock('react-i18next', ...)` with a partial mock (just `useTranslation`) - Vitest's
      // strict mock proxy throws on the missing `initReactI18next` export, crashing those files
      // at collection time before any of their own tests run (AB#1751 review). Deferring the
      // import to the moment a network/timeout error actually happens means only a test that
      // exercises this exact branch needs a full react-i18next mock - i18n.t() still resolves
      // against whatever language is active right now, same as every other message built at
      // reject time in this codebase (e.g. mapBackendErrors.ts).
      const { default: i18n } = await import('./i18n');
      return Promise.reject(new Error(
        i18n.t(isTimeout ? 'common.errors.timeout' : 'common.errors.network'),
      ));
    }

    const original = err.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = err.response.status;
    const url = original?.url ?? '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

    // Billing access gate (docs/design/billing-access-gate.md): TenantAccessGateMiddleware
    // returns 402 for any tenant-scope request once the tenant is Blocked. Pushing the
    // verdict into authStore here — instead of waiting for the next login/refresh — means
    // SubscriptionGuard flips to the full-screen block on the very request that tripped it,
    // not on whatever page the user happens to navigate to next. No toast: the guard screen
    // itself is the notification, and a toast on top would double-announce the same fact.
    const body = err.response.data as
      | { message?: string; reason?: BillingAccessReason; accessEndsAt?: string | null }
      | undefined;
    if (status === 402 && body?.message === BILLING_ACCESS_BLOCKED_ERROR_KEY) {
      useAuthStore.getState().setBillingAccess({
        enforced: true,
        accessState: 'Blocked',
        reason: body.reason ?? BILLING_ACCESS_REASONS.None,
        accessEndsAt: body.accessEndsAt ?? null,
      });
      return Promise.reject(err);
    }

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      const outcome = await refreshSingleFlight();

      if (outcome.status === RefreshStatuses.refreshed) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${outcome.token}`;
        return api(original);
      }

      if (outcome.status === RefreshStatuses.unavailable) {
        // AB#2112: the server did not say the session is over, it just could not answer. Keep
        // the token and refresh cookie; reject with a typed, retryable error instead.
        console.warn('[API] Refresh geçici olarak başarısız – oturum korunuyor.');
        return Promise.reject(await notifyRefreshUnavailable());
      }

      console.warn('[API] Refresh başarısız – oturum sonlandırılıyor.');
      redirectToLogin(outcome.reasonKey);
    } else if (status === 401) {
      // Retry sonrası veya auth endpoint'te 401 → oturumu kapat. Auth endpoint'inin kendisi
      // 401 döndüyse sebep dogrudan bu yanitta.
      redirectToLogin(isAuthEndpoint ? extractSessionEndReason(err) : null);
    }

    return Promise.reject(err);
  }
);

export default api;
